import { createSlice } from "@reduxjs/toolkit";
import { Dispatch } from "react";
import { Microservices } from "../../app/AllowedMicroservices";
import { RootState } from "../../app/store";
import apiActionCreator, { HttpMethods } from "../../services/apiActionCreator";
import { FetchStatus } from "../common/enums";

// Mirrors PUT /progress-audit/progress/:progressId/course/finish
// (service-learning, SuperAdmin only) - marks a course finished when every
// topic/lesson/exam is really complete but courseProgress.progress never
// reached 1 (the client apparently never sent the final
// {courseId, progress:1} - SYSTEM.debt.md ítem 29/§15.29). Can come back
// with an explicit error (course not actually complete), so the backend's
// message is surfaced instead of a generic one.
const DEFAULT_ERROR_MESSAGE = "No se pudo marcar el curso como terminado";

interface ProgressAuditFixCourseState {
  status: FetchStatus;
}

const initialState: ProgressAuditFixCourseState = {
  status: FetchStatus.Idle,
};

export const progressAuditFixCourseSlice = createSlice({
  name: "progressAuditFixCourse",
  initialState,
  reducers: {
    fixing: (state) => {
      state.status = FetchStatus.Fetching;
    },
    fixed: (state) => {
      state.status = FetchStatus.Fetched;
    },
    error: (state) => {
      state.status = FetchStatus.Error;
    },
  },
});

export const { fixing, fixed, error } = progressAuditFixCourseSlice.actions;

export const fixProgressAuditCourse = (
  progressId: string,
  onSuccess: () => void = () => {},
  onError: (message: string) => void = () => {}
) => (dispatch: Dispatch<object>) => {
  return dispatch(
    apiActionCreator({
      endpoint: `/progress-audit/progress/${progressId}/course/finish`,
      types: {
        requestType: fixing,
        successTypes: [
          {
            actionOrCreator: fixed,
            selector: () => {
              onSuccess();
            },
          },
        ],
        errorTypes: [
          {
            actionOrCreator: error,
            selector: (response: { data?: { data?: string } }) => {
              onError(response?.data?.data || DEFAULT_ERROR_MESSAGE);
            },
          },
        ],
      },
      method: HttpMethods.PUT,
      microservice: Microservices.LEARNING_SERVICE,
      authenticated: true,
    })
  );
};

export const selectIsFixingProgressAuditCourse = (state: RootState) =>
  state.progressAuditFixCourse.status === FetchStatus.Fetching;

export default progressAuditFixCourseSlice.reducer;
